// Human labels for the API enums (modality, value type, flags, reference point, base, statuses, quantity kinds, curve types, job kinds) used across screens.
import type {
  BaseQuantity,
  ClauseFlag,
  CurveType,
  DocumentStatus,
  ExtractionStatus,
  JobKind,
  JobStatus,
  Modality,
  ParameterStatus,
  QualityFlag,
  QuantityKind,
  ReferencePoint,
  RunStatus,
  ValueType,
} from "@/api/__generated__";

export const MODALITY_LABELS: Record<Modality, string> = {
  shall: "Shall",
  shall_not: "Shall not",
  should: "Should",
  should_not: "Should not",
  may: "May",
};

export const VALUE_TYPE_LABELS: Record<ValueType, string> = {
  scalar: "Value",
  range: "Range",
  curve: "Curve",
  curve_family: "Curve family",
  bands: "Bands",
  boolean: "Yes or no",
  text: "Text",
};

export const QUALITY_FLAG_LABELS: Record<QualityFlag, string> = {
  low_confidence: "Low confidence",
  citation_not_verbatim: "Citation not verbatim",
  number_not_in_source: "Number not in the source",
  base_other: "Base unclear",
  base_missing: "Base missing",
  reference_point_other: "Reference point unclear",
  condition_other: "Condition unclear",
  applicability_issue: "Applicability issue",
  parameter_uncertain: "Parameter uncertain",
  recheck_dropped: "Dropped on re-check",
  model_fallback: "Fallback model used",
  curve_table_mismatch: "Curve and table differ",
  figure_digitized: "Digitised from a figure",
  figure_not_found: "Figure not found",
  figure_not_digitized: "Figure not digitised",
  translation_unverified: "Translation not verified",
  obligation_scan_pivot_only: "Obligation scan on the English translation only",
  obligation_scan_unavailable: "Obligation scan unavailable",
  language_mismatch: "Language differs from the registry",
  synthetic_numbering: "Numbering derived from headings",
};

export const CLAUSE_FLAG_LABELS: Record<ClauseFlag, string> = {
  obligation_gap: "Obligation gap",
  informative_with_obligations: "Informative with obligations",
  extraction_failed: "Extraction failed",
  extraction_refused: "Extraction refused",
  requirement_invalid: "Invalid requirement",
};

export const REFERENCE_POINT_LABELS: Record<ReferencePoint, string> = {
  connection_point: "Connection point",
  interface_point: "Interface point",
  unit_terminals: "Unit terminals",
  unit_transformer_hv: "Unit transformer HV side",
  collection_busbar: "Collection busbar",
  not_applicable: "Not applicable",
  other: "Other",
};

export const BASE_LABELS: Record<BaseQuantity, string> = {
  p_max: "Pmax",
  p_n: "Pn",
  s_n: "Sn",
  registered_capacity: "Registered capacity",
  p_actual: "Actual power",
  p_ref: "Pref",
  u_c: "Uc",
  u_n: "Un",
  i_n: "In",
  f_n: "fn",
  absolute: "Absolute",
  not_applicable: "Not applicable",
  other: "Other",
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: "Draft",
  published: "Published",
  in_force: "In force",
  superseded: "Superseded",
  archived: "Archived",
};

export const EXTRACTION_STATUS_LABELS: Record<ExtractionStatus, string> = {
  not_in_scope: "Not in scope",
  pending: "Pending",
  extracted: "Extracted",
  reused: "Reused",
  informative: "Informative",
  failed: "Failed",
};

export const PARAMETER_STATUS_LABELS: Record<ParameterStatus, string> = {
  approved: "Approved",
  proposed: "Proposed",
  deprecated: "Deprecated",
};

export const QUANTITY_KIND_LABELS: Record<QuantityKind, string> = {
  voltage: "Voltage",
  active_power: "Active power",
  reactive_power: "Reactive power",
  apparent_power: "Apparent power",
  current: "Current",
  reactive_current: "Reactive current",
  frequency: "Frequency",
  rate_of_change_of_frequency: "RoCoF",
  time: "Time",
  power_factor: "Power factor",
  angle: "Angle",
  ratio: "Ratio",
  droop: "Droop",
  ramp_rate: "Ramp rate",
  energy: "Energy",
  harmonics: "Harmonics",
  flicker: "Flicker",
  temperature: "Temperature",
  boolean: "Yes or no",
  count: "Count",
  other: "Other",
};

export const RUN_STATUS_LABELS: Record<RunStatus | "interrupted", string> = {
  running: "Running",
  succeeded: "Succeeded",
  partial: "Partial",
  failed: "Failed",
  rolled_back: "Rolled back",
  interrupted: "Interrupted",
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
  interrupted: "Interrupted",
};

export const JOB_KIND_LABELS: Record<JobKind, string> = {
  ingest: "Ingestion",
  ingest_resume: "Resume ingestion",
  ingest_dry_run: "Ingestion dry run",
  rollback: "Rollback",
  delete_version: "Delete version",
  consolidate_parameters: "Consolidate parameters",
  source_watch: "Source check",
  evaluation: "Evaluation",
  source_profile: "Source profile",
  capability_extract: "Capability document reading",
  assessment_run: "Compliance assessment",
};

export const CURVE_TYPE_LABELS: Record<CurveType, string> = {
  frt_lvrt: "LVRT",
  frt_hvrt: "HVRT",
  voltage_time: "Voltage–time",
  frequency_time: "Frequency–time",
  pq_capability: "P–Q capability",
  uq_capability: "U–Q capability",
  p_f: "P–f",
  q_u: "Q–U",
  k_factor: "k-factor",
  ramp: "Ramp",
  other: "Other",
};

export type Tone = "ok" | "grey" | "attn";

export function labelOf<T extends string>(
  labels: Partial<Record<T, string>>,
  value: T | null | undefined,
): string {
  if (value === null || value === undefined) {
    return "–";
  }
  return labels[value] ?? value.replaceAll("_", " ");
}
